import React from 'react';
import T from 'i18n-react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import deburr from 'lodash/deburr';
import Autosuggest from 'react-autosuggest';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import TextField from '@material-ui/core/TextField';
import Paper from '@material-ui/core/Paper';
import MenuItem from '@material-ui/core/MenuItem';
import Popper from '@material-ui/core/Popper';
import { actAnyFieldUpdate, getValue } from 'actions/formActions';
import { withStyles } from '@material-ui/core/styles';

const renderInputComponent = (inputProps) => {
  const { classes, inputRef = () => {}, ref, ...other } = inputProps;

  return (
    <TextField
      fullWidth
      InputProps={{
        inputRef: (node) => {
          ref(node);
          inputRef(node);
        },
        classes: {
          input: classes.input,
        },
      }}
      {...other}
    />
  );
};

const renderSuggestion = (suggestion, { query, isHighlighted }) => {
  const matches = match(suggestion.label, query);
  const parts = parse(suggestion.label, matches);

  return (
    <MenuItem selected={isHighlighted} component='div'>
      <div>
        {parts.map((part, index) =>
          part.highlight ? (
            <span key={String(index)} style={{ fontWeight: 500 }}>
              {part.text}
            </span>
          ) : (
            <strong key={String(index)} style={{ fontWeight: 300 }}>
              {part.text}
            </strong>
          )
        )}
      </div>
    </MenuItem>
  );
};

const getSuggestionValue = (suggestion) => suggestion.value;

const styles = (theme) => ({
  root: {
    marginTop: '10px',
    marginBottom: '8px',
    width: '100%',
    flexGrow: 1,
    '& label, input': {
      paddingLeft: '10px',
      paddingRight: '10px',
    },
  },
  container: {
    position: 'relative',
  },
  suggestionsContainerOpen: {
    zIndex: '1050 !important',
    marginTop: theme.spacing(1),
  },
  suggestion: {
    display: 'block',
  },
  suggestionsList: {
    margin: 0,
    padding: 0,
    listStyleType: 'none',
  },
  divider: {
    height: theme.spacing(2),
  },
  labelDiv: {
    display: 'flex',
  },
  mandatory: {
    fontSize: '20px',
    color: 'red',
    marginRight: '10px',
  },
});

class FormAutoComplete extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      suggestions: [],
    };

    this.myValue = this.myValue.bind(this);
    this.getSuggestions = this.getSuggestions.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleSuggestionsFetchRequested = this.handleSuggestionsFetchRequested.bind(this);
    this.handleSuggestionsClearRequested = this.handleSuggestionsClearRequested.bind(this);
  }

  getSuggestions({ value }) {
    const { choices } = this.props;
    const noOfChoices = this.props.noOfChoices || 5;
    const inputValue = deburr(value.trim()).toLowerCase();
    const inputLength = inputValue.length;
    let count = 0;

    return inputLength === 0
      ? []
      : choices.filter((suggestion) => {
          const keep =
            count < noOfChoices &&
            suggestion.label.slice(0, inputLength).toLowerCase() === inputValue;
          if (keep) {
            count += 1;
          }

          return keep;
        });
  }

  handleSuggestionsFetchRequested(value) {
    this.setState({
      suggestions: this.getSuggestions(value),
    });
  }

  handleSuggestionsClearRequested() {
    this.setState({
      suggestions: [],
    });
  }

  handleChange(event, { newValue, method }) {
    const { storeName, accessPath, onFieldUpdate } = this.props;
    onFieldUpdate(storeName, accessPath, newValue);
  }

  myValue(storeName, accessPath) {
    return getValue(this.props[storeName].data, accessPath)
      ? getValue(this.props[storeName].data, accessPath)
      : '';
  }

  render() {
    const {
      classes,
      choices,
      storeName,
      accessPath,
      fieldName,
      placeholder,
      noLabel,
      mandatory,
      onModal,
      shrink,
      disabled,
    } = this.props;

    const autosuggestProps = {
      renderInputComponent,
      suggestions: this.state.suggestions,
      onSuggestionsFetchRequested: this.handleSuggestionsFetchRequested,
      onSuggestionsClearRequested: this.handleSuggestionsClearRequested,
      getSuggestionValue,
      renderSuggestion,
    };

    const newValue = this.myValue(storeName, accessPath);

    return (
      <div className={classes.root}>
        <Autosuggest
          {...autosuggestProps}
          inputProps={{
            classes,
            label: noLabel ? null : !fieldName ? (
              <div className={classes.labelDiv}>
                {mandatory ? <div className={classes.mandatory}>*</div> : null}
                {T.translate(`${storeName}.${accessPath[accessPath.length - 1]}`)}
              </div>
            ) : (
              <div className={classes.labelDiv}>
                {mandatory ? <div className={classes.mandatory}>*</div> : null}
                {T.translate(`${storeName}.${fieldName}`)}
              </div>
            ),
            disabled,
            placeholder: placeholder || '',
            value: choices.find((c) => c.value === newValue)
              ? choices.find((c) => c.value === newValue).label
              : newValue,
            onChange: this.handleChange,
            inputRef: (node) => {
              this.popperNode = node;
            },
            InputLabelProps: { shrink },
          }}
          theme={{
            suggestionsList: classes.suggestionsList,
            suggestion: classes.suggestion,
            suggestionsContainerOpen: classes.suggestionsContainerOpen,
          }}
          renderSuggestionsContainer={(options) =>
            onModal ? (
              <Paper square {...options.containerProps}>
                {options.children}
              </Paper>
            ) : (
              <Popper anchorEl={this.popperNode} open={Boolean(options.children)}>
                <Paper
                  square
                  {...options.containerProps}
                  style={{
                    width: this.popperNode ? this.popperNode.clientWidth : null,
                  }}>
                  {options.children}
                </Paper>
              </Popper>
            )
          }
        />
      </div>
    );
  }
}

const mapStateToProps = (state) => state;

const mapDispatchToProps = (dispatch) => ({
  onFieldUpdate: (storeName, accessPath, newVal) => {
    dispatch(actAnyFieldUpdate(storeName, accessPath, newVal));
  },
});

FormAutoComplete.propTypes = {
  classes: PropTypes.object.isRequired,
  storeName: PropTypes.string.isRequired,
  accessPath: PropTypes.array.isRequired,
  placeholder: PropTypes.string,
  noLabel: PropTypes.bool,
  mandatory: PropTypes.bool,
};

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(FormAutoComplete));
